import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Receipt,
  Search,
  Payment,
  ContentCopy,
  CheckCircle,
  Schedule,
  Warning,
  Close,
  CalendarToday,
  QrCodeScanner,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/api';

interface BoletoConsulta {
  numeroInstituicaoEmissora: number;
  nomeInstituicaoEmissora: string;
  tipoPessoaBeneficiario: string;
  numeroCpfCnpjBeneficiario: string;
  nomeRazaoSocialBeneficiario: string;
  nomeFantasiaBeneficiario?: string;
  tipoPessoaPagador: string;
  numeroCpfCnpjPagador: string;
  nomeRazaoSocialPagador: string;
  codigoBarras: string;
  numeroLinhaDigitavel: string;
  dataVencimentoBoleto: string;
  dataLimitePagamentoBoleto?: string;
  valorBoleto: number;
  valorAbatimentoDesconto: number;
  valorMultaMora: number;
  valorPagamento: number;
  dataPagamento?: string;
  permiteAlterarValor: boolean;
  identificadorConsulta: string;
  bloquearPagamento?: boolean;
  mensagemBloqueioPagamento?: string;
  nossoNumero?: string;
  numeroDocumento?: string;
}

interface PagamentoResultado {
  idPagamento: number;
  situacaoPagamento: string;
  dataPagamento: string;
  valorPagamento: number;
  numeroAutenticacaoPagamento: string;
  descricaoTituloComprovante?: string;
}

const PagarBoletoPage: React.FC = () => {
  const [codigoBarras, setCodigoBarras] = useState('');
  const [dataPagamento, setDataPagamento] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [observacao, setObservacao] = useState('');

  const [boletoData, setBoletoData] = useState<BoletoConsulta | null>(null);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pagamentoResultado, setPagamentoResultado] = useState<PagamentoResultado | null>(null);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatCpfCnpj = (value: string) => {
    if (!value) return '-';
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return numbers
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  };

  const formatLinhaDigitavel = (value: string) => {
    if (!value) return '';
    // Format linha digitável with spaces every 5 digits for readability
    return value.replace(/(.{5})/g, '$1 ').trim();
  };

  const handleCodigoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove non-numeric characters except dots and spaces
    const value = e.target.value.replace(/[^\d\s.]/g, '');
    setCodigoBarras(value);
    // Clear previous data when typing new code
    if (boletoData) {
      setBoletoData(null);
    }
  };

  const handleConsultar = async () => {
    const codigo = codigoBarras.replace(/\D/g, '');

    if (codigo.length !== 44 && codigo.length !== 47) {
      setError('Código de barras deve ter 44 dígitos ou linha digitável deve ter 47 dígitos');
      return;
    }

    setLoading(true);
    setError(null);
    setBoletoData(null);

    try {
      const params: Record<string, string> = {};
      if (dataPagamento) {
        params.dataPagamento = dataPagamento;
      }

      const response = await api.get(`/pagamentos/boletos/${codigo}`, { params });

      if (response.data.success && response.data.data) {
        setBoletoData(response.data.data);

        if (response.data.data.bloquearPagamento) {
          setError(response.data.data.mensagemBloqueioPagamento || 'Pagamento bloqueado');
        }
      } else {
        setError('Boleto não encontrado');
      }
    } catch (err: any) {
      console.error('Error consulting boleto:', err);
      setError(err.response?.data?.error?.message || 'Erro ao consultar boleto');
    } finally {
      setLoading(false);
    }
  };

  const handlePagar = async () => {
    if (!boletoData) return;

    setPaying(true);
    setError(null);

    try {
      const codigo = codigoBarras.replace(/\D/g, '');

      const response = await api.post(`/pagamentos/boletos/${codigo}/pagar`, {
        identificadorConsulta: boletoData.identificadorConsulta,
        valorBoleto: boletoData.valorBoleto,
        valorDescontoAbatimento: boletoData.valorAbatimentoDesconto || 0,
        valorMultaMora: boletoData.valorMultaMora || 0,
        aceitaValorDivergente: boletoData.permiteAlterarValor,
        dataPagamento,
        descricaoObservacao: observacao || undefined,
      });

      if (response.data.success) {
        setPagamentoResultado(response.data.data);
        setConfirmDialogOpen(false);
        setResultDialogOpen(true);
        setSuccess('Pagamento realizado com sucesso!');
        // Clear form
        setCodigoBarras('');
        setBoletoData(null);
        setObservacao('');
      }
    } catch (err: any) {
      console.error('Error paying boleto:', err);
      setError(err.response?.data?.error?.message || 'Erro ao pagar boleto');
      setConfirmDialogOpen(false);
    } finally {
      setPaying(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        <Payment sx={{ mr: 1, verticalAlign: 'middle' }} />
        Pagar Boleto
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Consulta de Boleto */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          <QrCodeScanner sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
          Consultar Boleto
        </Typography>

        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Código de Barras ou Linha Digitável"
              value={codigoBarras}
              onChange={handleCodigoChange}
              placeholder="Digite ou cole o código de barras"
              helperText="44 dígitos (código de barras) ou 47 dígitos (linha digitável)"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Receipt color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Data de Pagamento"
              value={dataPagamento}
              onChange={(e) => setDataPagamento(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Deixe em branco para pagar hoje"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleConsultar}
              disabled={loading || codigoBarras.replace(/\D/g, '').length < 44}
              startIcon={loading ? <CircularProgress size={20} /> : <Search />}
              sx={{ height: 56 }}
            >
              {loading ? 'Consultando...' : 'Consultar'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Dados do Boleto */}
      {boletoData && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Dados do Boleto
            </Typography>
            {boletoData.bloquearPagamento ? (
              <Chip icon={<Warning />} label="Pagamento Bloqueado" color="error" />
            ) : (
              <Chip icon={<CheckCircle />} label="Disponível para Pagamento" color="success" />
            )}
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            {/* Linha Digitável */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    Linha Digitável
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" fontFamily="monospace" sx={{ flex: 1 }}>
                      {formatLinhaDigitavel(boletoData.numeroLinhaDigitavel)}
                    </Typography>
                    <Tooltip title="Copiar">
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(boletoData.numeroLinhaDigitavel)}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Valores */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Valores
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Valor do Boleto:</Typography>
                      <Typography variant="body2">{formatCurrency(boletoData.valorBoleto)}</Typography>
                    </Box>
                    {boletoData.valorAbatimentoDesconto > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Desconto/Abatimento:</Typography>
                        <Typography variant="body2" color="success.main">
                          - {formatCurrency(boletoData.valorAbatimentoDesconto)}
                        </Typography>
                      </Box>
                    )}
                    {boletoData.valorMultaMora > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Multa/Mora:</Typography>
                        <Typography variant="body2" color="error.main">
                          + {formatCurrency(boletoData.valorMultaMora)}
                        </Typography>
                      </Box>
                    )}
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" fontWeight="bold">Total a Pagar:</Typography>
                      <Typography variant="body1" fontWeight="bold" color="primary.main">
                        {formatCurrency(boletoData.valorPagamento)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Datas */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Datas
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Vencimento:</Typography>
                      <Typography variant="body2">
                        {boletoData.dataVencimentoBoleto
                          ? format(parseISO(boletoData.dataVencimentoBoleto), 'dd/MM/yyyy')
                          : '-'}
                      </Typography>
                    </Box>
                    {boletoData.dataLimitePagamentoBoleto && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Limite Pagamento:</Typography>
                        <Typography variant="body2">
                          {format(parseISO(boletoData.dataLimitePagamentoBoleto), 'dd/MM/yyyy')}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Data Pagamento:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {dataPagamento ? format(parseISO(dataPagamento), 'dd/MM/yyyy') : 'Hoje'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Beneficiário */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Beneficiário (Recebedor)
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Typography variant="body2" fontWeight="medium">
                    {boletoData.nomeRazaoSocialBeneficiario}
                  </Typography>
                  {boletoData.nomeFantasiaBeneficiario && (
                    <Typography variant="body2" color="text.secondary">
                      {boletoData.nomeFantasiaBeneficiario}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {formatCpfCnpj(boletoData.numeroCpfCnpjBeneficiario)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {boletoData.nomeInstituicaoEmissora}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Pagador */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Pagador
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Typography variant="body2" fontWeight="medium">
                    {boletoData.nomeRazaoSocialPagador || '-'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCpfCnpj(boletoData.numeroCpfCnpjPagador)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Observação */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observação (opcional)"
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                placeholder="Ex: Pagamento referente a..."
                multiline
                rows={2}
              />
            </Grid>

            {/* Botão Pagar */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setBoletoData(null);
                    setCodigoBarras('');
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => setConfirmDialogOpen(true)}
                  disabled={boletoData.bloquearPagamento}
                  startIcon={<Payment />}
                >
                  Pagar {formatCurrency(boletoData.valorPagamento)}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Dialog de Confirmação */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirmar Pagamento</DialogTitle>
        <DialogContent>
          {boletoData && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Confirme os dados antes de prosseguir. Esta ação não pode ser desfeita.
              </Alert>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Beneficiário:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {boletoData.nomeRazaoSocialBeneficiario}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Valor:</Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary.main">
                    {formatCurrency(boletoData.valorPagamento)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Data:</Typography>
                  <Typography variant="body2">
                    {dataPagamento ? format(parseISO(dataPagamento), 'dd/MM/yyyy') : 'Hoje'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={paying}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handlePagar}
            disabled={paying}
            startIcon={paying ? <CircularProgress size={20} /> : <Payment />}
          >
            {paying ? 'Processando...' : 'Confirmar Pagamento'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Resultado */}
      <Dialog open={resultDialogOpen} onClose={() => setResultDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle color="success" />
          Pagamento Realizado
        </DialogTitle>
        <DialogContent>
          {pagamentoResultado && (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                {pagamentoResultado.situacaoPagamento === 'Agendado'
                  ? 'Pagamento agendado com sucesso!'
                  : 'Pagamento realizado com sucesso!'}
              </Alert>

              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">ID do Pagamento:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {pagamentoResultado.idPagamento}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Situação:</Typography>
                      <Chip
                        label={pagamentoResultado.situacaoPagamento}
                        size="small"
                        color={pagamentoResultado.situacaoPagamento === 'Efetivado' ? 'success' : 'warning'}
                        icon={pagamentoResultado.situacaoPagamento === 'Efetivado' ? <CheckCircle /> : <Schedule />}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Valor:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="primary.main">
                        {formatCurrency(pagamentoResultado.valorPagamento)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Data:</Typography>
                      <Typography variant="body2">
                        {pagamentoResultado.dataPagamento
                          ? format(parseISO(pagamentoResultado.dataPagamento), 'dd/MM/yyyy')
                          : '-'}
                      </Typography>
                    </Box>
                    {pagamentoResultado.numeroAutenticacaoPagamento && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">Autenticação:</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" fontFamily="monospace">
                            {pagamentoResultado.numeroAutenticacaoPagamento}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(pagamentoResultado.numeroAutenticacaoPagamento)}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultDialogOpen(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PagarBoletoPage;
